"use client";

import Link from "next/link";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import type { BlogEntry } from "@/lib/cms";

const VISIBLE_COUNT = 3;

const BlogCard = ({ post }: { post: BlogEntry }) => {
  const { t } = useTranslation();

  return (
    <Link href={`/blogs/${post.slug}`} className="block">
      <article className="bg-surface-card rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow h-full flex flex-col">
        {post.image && (
          <div className="h-48 overflow-hidden group">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-110"
            />
          </div>
        )}
        <div className="p-5 flex flex-col flex-1">
          <div className="flex items-center gap-3 mb-2">
            {post.category && (
              <span className="font-body text-xs text-foreground/70">{post.category}</span>
            )}
            {post.readTime && (
              <span className="font-body text-xs text-foreground/50">{post.readTime}</span>
            )}
          </div>
          <h3 className="font-heading font-medium text-base text-foreground mb-2 line-clamp-2">
            {post.title}
          </h3>
          <p className="font-body text-xs text-foreground/70 mb-3 line-clamp-2">{post.excerpt}</p>
          <span className="inline-flex items-center gap-1 font-body font-medium text-xs text-foreground hover:text-accent-blue mt-auto">
            {t("blog.readMore")} <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </article>
    </Link>
  );
};

type BlogProps = { posts: BlogEntry[] };

const Blog = ({ posts }: BlogProps) => {
  const { t } = useTranslation();
  const [startIndex, setStartIndex] = useState(0);
  const totalPosts = posts.length;
  const canPrev = startIndex > 0;
  const canNext = startIndex + VISIBLE_COUNT < totalPosts;
  const [isAnimating, setIsAnimating] = useState(false);

  const visiblePosts = posts.slice(startIndex, startIndex + VISIBLE_COUNT);

  const handleNav = (direction: "prev" | "next") => {
    if (isAnimating) return;
    setIsAnimating(true);
    if (direction === "prev") {
      setStartIndex((i) => Math.max(0, i - 1));
    } else {
      setStartIndex((i) => Math.min(totalPosts - VISIBLE_COUNT, i + 1));
    }
    setTimeout(() => setIsAnimating(false), 400);
  };

  if (totalPosts === 0) return null;

  return (
    <section className="bg-surface-light py-20 px-6 lg:px-16" id="blog">
      <div className="max-w-[1100px] mx-auto">
        {/* Section Header */}
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground mb-2">
              {t("blog.heading")}
            </h2>
            <p className="font-body text-base text-foreground/70">
              {t("blog.subtitle")}
            </p>
          </div>

          {totalPosts > VISIBLE_COUNT && (
            <div className="hidden md:flex items-center gap-2 flex-shrink-0 ml-6">
              <button
                onClick={() => handleNav("prev")}
                disabled={!canPrev || isAnimating}
                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                  canPrev
                    ? "border-accent-blue text-accent-blue hover:bg-accent-blue hover:text-white"
                    : "border-foreground/20 text-foreground/20 cursor-not-allowed"
                }`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleNav("next")}
                disabled={!canNext || isAnimating}
                className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
                  canNext
                    ? "border-accent-blue text-accent-blue hover:bg-accent-blue hover:text-white"
                    : "border-foreground/20 text-foreground/20 cursor-not-allowed"
                }`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Blog Cards with fade transition */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {visiblePosts.map((post, idx) => (
            <div
              key={`${startIndex}-${idx}`}
              className="animate-fade-in"
              style={{ animationDelay: `${idx * 80}ms`, animationFillMode: "both" }}
            >
              <BlogCard post={post} />
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link href="/blogs">
            <Button
              variant="outline"
              className="border-accent-blue text-accent-blue hover:bg-accent-blue hover:text-white rounded-full px-6 py-3 font-button text-sm cta-hover"
            >
              {t("blog.viewAll")}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Blog;
